
import { useNetwork } from '@/src/contexts';
import { getNetworkConfig } from '@/src/helpers';
import { useEffect, useState } from 'react';
import { Client } from 'xrpl';
import { useXRPLSubscription } from './use-xrpl-subscription';

/**
 * A hook to interact with [XRPL.js](https://js.xrpl.org)
 *
 * @param options An object with the following properties:
 * - wssUrl: xrpl websocket url to connect to
 * - onConnected: function to call when client connects
 * - onDisconnected: function to call when client disconnects
 * @returns An object with the following properties:
 * - client: [xrpl.Client](https://js.xrpl.org/classes/Client.html)
 * - isConnected: undefined = connecting true = connected false = disconnected
 */
export const useXRPL = (
  options: {
    wssUrl?: string;
    onConnected?: () => void;
    onDisconnected?: () => void;
  } = {}
) => {
  // TODO: move this useNetwork to the contexts directory
  const { network } = useNetwork();

  const opts = {
    wssUrl: getNetworkConfig(network).wsUrl,
    onConnected: () => undefined,
    onDisconnected: () => undefined,
    ...options,
  };

  const [client, setClient] = useState<Client>(
    () =>
      new Client(opts.wssUrl, {
        connectionTimeout: 15000, // 15 seconds instead of default 5
        timeout: 20000, // 20 seconds for request timeout
      })
  );
  const [isConnected, setIsConnected] = useState<boolean>();

  // Recreate client when network/wssUrl changes
  useEffect(() => {
    const newClient = new Client(opts.wssUrl, {
      connectionTimeout: 15000,
      timeout: 20000,
    });
    setClient(newClient);

    return () => {
      newClient.disconnect();
    };
  }, [opts.wssUrl]);

  useXRPLSubscription(client, {
    connected: () => {
      console.debug('[xrpl] connected.', client.url);
      setIsConnected(true);
      opts.onConnected();
    },
    disconnected: () => {
      console.debug('[xrpl] disconnected.', client.url);
      setIsConnected(false);
      opts.onDisconnected();
    },
    error: () => {
      console.debug('[xrpl] error.');
    },
  });

  useEffect(() => {
    console.debug('[xrpl] connecting...', client.url);

    client.connect();

    return () => {
      console.debug('[xrpl] disconnecting...', client.url);
      client.disconnect();
    };
  }, [client]);

  return {
    client,
    isConnected,
  };
};
